import React, { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import toast, { Toaster } from "react-hot-toast";
import Lottie from "lottie-react";
import heroAnimation from "../assets/animation-16.json";

const Hero = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });
  
  // Scroll-based animations
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 150]);
  const textY = useTransform(scrollY, [0, 300], [0, -50]);
  const imageScale = useTransform(scrollY, [0, 300], [1, 0.8]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.7]); // Changed to fade slower and less

  // Handle email change
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Construct the data object you need to send to Mailchimp
    const data = new FormData();
    data.append("EMAIL", email);

    // Mailchimp form action URL
    const mailchimpUrl =
      "https://app.us13.list-manage.com/subscribe/post?u=192fbe929caf4d95a1d7fcd7b&amp;id=d6f9423fb0&amp;f_id=00cfc2e1f0";

    try {
      await fetch(mailchimpUrl, {
        method: "POST",
        body: data,
        mode: "no-cors",
      });
      
      toast.success("Thanks for subscribing! We'll keep you updated.", {
        duration: 4000,
        position: "bottom-center",
        style: {
          background: "#8ABB01",
          color: "#fff",
          padding: "16px",
          borderRadius: "50px",
        },
      });
      setEmail("");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster />
      <section className="relative bg-[#F2FCDA] hero-section overflow-hidden min-h-screen flex items-center" id="hero">
        <motion.div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: "url('LooperBG.png')",
            backgroundSize: "cover",
            backgroundPosition: "left top",
            y: backgroundY,
          }}
        />
        {/* Navbar is now rendered at App level */}
        <div className="container mx-auto relative z-50 pt-14 md:pt-28 px-4" ref={ref}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-0 md:gap-0 items-center min-h-[calc(100vh-100px)] md:h-[600px] lg:h-[1000px] pb-6 md:pb-0">
            <motion.div
              className="md:text-left text-center -mt-18 sm:-mt-8 md:mt-0 lg:ml-[-30px] order-2 md:order-1"
              style={{ y: textY, opacity }}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="text-[26px] sm:text-[32px] md:text-[40px] lg:text-[60px] font-bold mb-3 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Achieve Your Goals With{" "}
                <motion.span
                  className="text-[#8ABB01]"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  Nudgie
                </motion.span>{" "}
                AI
              </motion.h1>
              <motion.p
                className="text-[14px] sm:text-[16px] md:text-[18px] lg:text-[22px] mb-6 px-4 md:px-0"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Master your habits and achieve your dreams with cutting-edge AI coaching.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <p className="text-[13px] sm:text-[14px] md:text-[16px] mb-3 text-gray-600">
                  Stay updated with tips, features, and success stories:
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center md:justify-start">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    required
                    className="px-4 sm:px-6 py-3 border-2 border-[#8ABB01] rounded-full transition-all duration-300 focus:outline-none focus:border-black focus:shadow-lg w-full sm:w-auto md:min-w-[300px] lg:min-w-[400px] lg:max-w-[400px]"
                    disabled={isSubmitting}
                  />
                  <motion.button
                    type="submit"
                    className={`px-6 sm:px-8 py-3 text-white rounded-full border-2 border-[#8ABB01] font-semibold transition-all duration-300 w-full sm:w-auto ${
                      isSubmitting 
                        ? "bg-gray-400 cursor-not-allowed" 
                        : "bg-[#8ABB01] hover:bg-black hover:scale-105"
                    }`}
                    whileHover={!isSubmitting ? { y: -2 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Subscribing..." : "Subscribe"}
                  </motion.button>
                </form>
              </motion.div>
            </motion.div>
            
            <motion.div
              className="relative order-1 md:order-2 mb-2 md:mb-0"
              style={{ scale: imageScale }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Lottie
                animationData={heroAnimation}
                loop
                autoplay
                id="hero-animation"
                className="h-[40vh] max-h-[720px] md:h-full w-full mx-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
